<template>
  <div
    class="chart-wrapper"
    :class="mode">
    <div
      class="content"
      :class="{ 'has-sidebar': isFullscreen }">
      <chart-header
        v-if="actions && echartsOptions"
        :actions="actions"
        :chart-options="chartOptions"
        :echarts-options="echartsOptions"
        :group-actions="groupActions"
        :mode="mode"
        :template-ref="templateRef"
        :is-fullscreen="isFullscreen"
        :chart-warnings="chartWarnings"
        :should-show-daily-cap="shouldShowDailyCap"
        :should-daily-cap-toggle-be-available="shouldDailyCapToggleBeAvailable"
        @show-daily-cap="showDailyCap"
        @chart-option-reset="reset"
        @chart-option-selected="optionSelected"
        @go-fullscreen="goFullscreen" />

      <div
        v-if="isLoading"
        class="chart-loader">
        <loader-small />
      </div>

      <div class="body">
        <ChartContent
          v-if="!isLoading"
          ref="chartContent"
          :margin-top="marginTop"
          :template-ref="templateRef"
          :chart-options="chartOptions"
          :is-fullscreen="isFullscreen"
          :theme="theme"
          @chart-rendered="chartRendered" />

        <b-table
          v-if="showTable && items.length > 0"
          :id="tableId"
          category="ChartWrapper"
          :columns="columns"
          :is-loading="false"
          :enable-client-side-sorting="true"
          :enable-column-reorder="false"
          :items="paginatedItems"
          :pagination="true"
          :show-filters="false"
          :total-count="items.length - 1"
          @page-index-changed="updatePageIndex"
          @page-size-changed="updatePageSize" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { getInstanceByDom } from 'echarts/core';
  import { DateTime } from 'luxon';
  import { capitalize, computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';

  import ChartTrendActionsUtils from '@/common/components/chart/chart-trend-actions';
  import ChartContent from '@/common/components/chart/ChartContent.vue';
  import ChartHeader from '@/common/components/chart/ChartHeader.vue';
  import LoaderSmall from '@/common/components/controls/LoaderSmall.vue';
  import BTable from '@/common/components/controls/table/Table.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import CellType from '@/common/enums/cell-type.enum';
  import ChartActionId from '@/common/enums/chart-action-id.enum';
  import DownloadActionType from '@/common/enums/chart-download-action.enum';
  import ChartEvents from '@/common/enums/chart-events.enum';
  import ChartMode from '@/common/enums/chart-mode.enum';
  import ChartTimezoneType from '@/common/enums/chart-timezone-type.enum';
  import ChartTrendType from '@/common/enums/chart-trend-type.enum';
  import ChartType from '@/common/enums/chart-type.enum';
  import LocalCacheKeys from '@/common/enums/local-cache-keys.enum';
  import TimeGranularity from '@/common/enums/time-granularity.enum';
  import ChartGroupAction from '@/common/models/chart/ChartGroupAction';
  import TimeseriesAxisX from '@/common/models/chart/TimeseriesAxisX';
  import Exception from '@/common/models/Exception';
  import { debounce } from '@/globals';
  import CellTypeFormatterUtils from '@/utils/CellTypeFormatterUtils';
  import DateFormatUtils from '@/utils/DateFormatUtils';
  import DownloadUtils from '@/utils/DownloadUtils';
  import LocalCache from '@/utils/LocalCache';
  import i18n from '@/vueI18n';

  import { IChartWarning } from './IChartWarning';
  import IShouldDailyCapToggleBeAvailable from './IShouldDailyCapToggleBeAvailable';

  const emit = defineEmits([
    ChartEvents.CHART_TYPE_UPDATED,
    ChartEvents.CHART_GRANULARITY_UPDATED,
    ChartEvents.GO_FULLSCREEN,
    ChartEvents.TIMEZONE_SELECTED,
    ChartEvents.GO_FULLSCREEN,
    ChartEvents.SHOW_DAILY_CAP,
  ]);

  const chartOptions = ref(null);
  const groupActions = ref([]);
  const isLoading = ref(true);
  const pageIndex = ref(0);
  const pageSize = ref(10);
  const selectedTimezone = ref(ChartTimezoneType.UNIT_LOCAL);
  const chartContent = useTemplateRef('chartContent');

  interface IProps {
    actions?: ChartGroupAction[];
    chartResolutionFromDashboard?: string;
    chartWarnings?: Record<string, IChartWarning>;
    dontTrimDataFromChart: boolean;
    echartsOptions: any;
    highlightCurrentPeriod: boolean;
    isFullscreen?: boolean;
    isImpressionsAdSpendChart?: boolean;
    marginTop: string;
    mode: string;
    name: string;
    shouldDailyCapToggleBeAvailable?: IShouldDailyCapToggleBeAvailable;
    shouldShowDailyCap: boolean;
    showFuture: boolean;
    showTable?: boolean;
    templateRef: string;
    theme: string;
  }

  const props = withDefaults(defineProps<IProps>(), {
    actions: null,
    chartResolutionFromDashboard: null,
    chartWarnings: null,
    dontTrimDataFromChart: false,
    echartsOptions: null,
    highlightCurrentPeriod: false,
    isFullscreen: false,
    isImpressionsAdSpendChart: false,
    marginTop: '-4.5rem',
    mode: ChartMode.NONE,
    name: null,
    shouldDailyCapToggleBeAvailable: null,
    shouldShowDailyCap: true,
    showFuture: false,
    showTable: false,
    templateRef: null,
    theme: null,
  });

  const colTypes = computed(() => {
    if (!props.echartsOptions) return [];

    const { xAxis, series } = props.echartsOptions;
    const firstXAxis = xAxis instanceof Array ? xAxis[0] : xAxis;
    return [firstXAxis.cellType, ...series.map((s) => s?.cellType)];
  });

  const columns = computed(() => {
    const columns = {};
    if (props.echartsOptions.xAxis) {
      const { name, cellType, data, axisLabel } = props.echartsOptions.xAxis;

      columns[name] = TableColumn.fromObject({
        label: name,
        type: cellType,
        property: name,
        source: data.map((d) => (axisLabel?.formatter ? axisLabel.formatter(d) : d)),
      });
      props.echartsOptions.series.forEach((serie) => {
        columns[serie.name] = TableColumn.fromObject({
          label: capitalize(serie.name),
          type: serie.cellType,
          source: serie.data,
          property: serie.name,
        });
      });
    } else {
      props.echartsOptions.series.forEach((serie) => {
        Object.keys(serie?.columnsKeys).forEach((key) => {
          const k = serie.columnsKeys[key];
          columns[key] = TableColumn.fromObject({
            label: capitalize(k.label),
            type: k.cellType,
            property: k.property,
          });
        });
      });
    }

    return columns;
  });

  const items = computed(() => {
    const items = [];
    props.echartsOptions.series.forEach((serie) => {
      serie.data.forEach((d, i) => {
        if (!items[i]) {
          items[i] = {};
        }

        if (serie.type === ChartType.LINE || serie.type === ChartType.BAR || serie.type === ChartType.AREA) {
          items[i][props.echartsOptions.xAxis.name] = props.echartsOptions.xAxis?.axisLabel?.formatter
            ? props.echartsOptions.xAxis.axisLabel.formatter(props.echartsOptions.xAxis.data[i])
            : props.echartsOptions.xAxis.data[i];
          const value = getChartCellValue(d);
          items[i][serie.name] = serie?.transform ? serie?.transform(value) : value;
        }

        if (serie.type === ChartType.PIE) {
          Object.keys(serie?.columnsKeys).forEach((key) => {
            const cellType = serie?.columnsKeys[key]?.cellType;
            items[i][key] = CellTypeFormatterUtils.formatCellByType(d[key], cellType);
          });
        }
      });
    });

    return items;
  });

  const localCacheKey = computed(() => {
    let key = `${LocalCacheKeys.CHART_OPTIONS}.${props.templateRef}`;
    if (props.isFullscreen) {
      key += '.fullscreen';
    }
    return key;
  });

  const paginatedItems = computed(() => {
    const end = pageIndex.value * pageSize.value;
    const start = end - pageSize.value;
    return items.value.slice(start, end);
  });

  const rows = computed(() => {
    const serie = props.echartsOptions.series[0];
    const rows = [];

    // console.log(props.echartsOptions.xAxis.cellType);
    switch (serie.type) {
      case ChartType.LINE:
      case ChartType.BAR:
        // push headers
        rows.push([
          `${props.echartsOptions.xAxis.name}${props.echartsOptions.xAxis.cellType === CellType.DATE_TIME ? ' - UTC' : ''}`,
          ...props.echartsOptions.series.map((s) => s.name),
        ]);
        // push data
        props.echartsOptions.xAxis.raw.forEach((d, i) => {
          const row = [];
          row.push(DateTime.fromMillis(d).toLocaleString(DateTime.DATE_SHORT));

          for (const s of props.echartsOptions.series) {
            const cellData = s.data[i];
            // The data can be a primitive type (string, number) or an object with a value property
            row.push(getChartCellValue(cellData));
          }
          rows.push(row);
        });
        break;
      case ChartType.PIE:
        // push headers
        rows.push([
          capitalize(i18n.global.t('names.name', { n: 1 })),
          capitalize(i18n.global.t('names.value', { n: 1 })),
        ]);
        // push data
        serie.data.forEach((d) => rows.push([d.name, d.value]));
        break;
      default:
        break;
    }

    return rows;
  });

  const tableId = computed(() => {
    return `${props.name}.table`;
  });

  const availableActions = {
    [ChartActionId.TYPE]: changeChartType,
    [ChartActionId.TIMEZONE]: changeTimezone,
    [ChartActionId.DOWNLOAD]: downloadAs,
    [ChartActionId.INTERVAL]: intervalSelection,
    [ChartActionId.TREND]: showTrend,
    [ChartActionId.FULLSCREEN]: goFullscreen,
  };

  watch(
    () => props.actions,
    () => {
      setupActions();
    },
  );

  watch(
    () => props.echartsOptions,
    () => {
      applyOptions();
      setupActions();
    },
  );

  watch(groupActions, () => {
    const localActions = getLocalCacheForCharts();

    if (props.echartsOptions && props.actions && groupActions.value && chartOptions.value && localActions) {
      localActions.forEach((local) => {
        const action = groupActions.value.find((a) => a.id === local.id);
        const selectedOption = action.options.find((o) => o.isSelected);
        if (selectedOption.id !== local.optionSelected) {
          availableActions[action.id](action, selectedOption);
        }
      });
    }
  });

  onMounted(() => {
    init();
  });

  /**
   * Apparently during https://hivestack.atlassian.net/browse/CAM-1542, a new type of data was introduced and now cells can either be
   * of primitive type (string, number) or an object with a value property.
   * This method returns the raw data contained in a chart point
   * @param {*} cellData
   * @returns {string | number}
   */
  function getChartCellValue(cellData) {
    return cellData != null && typeof cellData === 'object' ? cellData.value : cellData;
  }

  function applyOptions() {
    isLoading.value = !props.echartsOptions;
    if (props.echartsOptions && props.echartsOptions.xAxis instanceof TimeseriesAxisX) {
      const settings = getLocalCacheForCharts();
      formatTimeSeriesChart({
        ...props.echartsOptions,
        series: props.echartsOptions.series.map((series) => ({
          ...series,
          type:
            settings != null
              ? settings.find((setting) => setting.id === ChartActionId.TYPE).optionSelected
              : series.type,
        })),
        xAxis: {
          ...props.echartsOptions.xAxis,
          granularity: props.chartResolutionFromDashboard
            ? props.chartResolutionFromDashboard
            : (chartOptions.value?.xAxis?.granularity ?? props.echartsOptions.xAxis.granularity),
        },
      });
    } else {
      chartOptions.value = {
        ...props.echartsOptions,
        yAxis: formatYAxis(props.echartsOptions?.xAxis?.granularity),
      };
    }
  }

  function chartRendered() {
    isLoading.value = false;
  }

  function changeChartType(action, option) {
    emit(ChartEvents.CHART_TYPE_UPDATED, option.id);
    /**
     * The use $nextTick helps us to update the chart after the $emit function has successfully finished.
     * Without using it the displayed data was wrong when changing chart types as it was loaded too fast
     */
    nextTick(() => {
      /**
       * We filter out the in progress data before sending it to the formatTimeSeriesChart function. This would cause the current period date to be
       * lost. Therefore, the data needs to be transferred back to the original one so that new in progress data is made from that data.
       */
      chartOptions.value.series.forEach((series) => {
        if (series.name?.includes(i18n.global.t('messages.inProgress'))) {
          const seriesNameWithoutInProgress = series.name.replace(` (${i18n.global.t('messages.inProgress')})`, '');

          const dataInProgress = series.data;
          chartOptions.value.series.find((s) => s.name === seriesNameWithoutInProgress).data = [
            ...chartOptions.value.series.find((s) => s.name === seriesNameWithoutInProgress).data,
            dataInProgress[dataInProgress.length - 1],
          ];
        }
      });
      // change series type to the selected chart option
      if (props.echartsOptions.xAxis instanceof TimeseriesAxisX) {
        formatTimeSeriesChart({
          ...props.echartsOptions,
          series: props.echartsOptions.series
            .filter((series) => !series.name?.includes(i18n.global.t('messages.inProgress')))
            .map((series) => ({
              ...series,
              type: option.id,
            })),
        });
      } else {
        chartOptions.value.series = chartOptions.value.series.map((serie) => ({
          ...serie,
          type: serie?.isTrend ? serie.type : option.id,
        }));
      }
    });
  }

  function changeTimezone(action, option) {
    selectedTimezone.value = option.id;
    const newDates = [];
    chartOptions.value.xAxis.raw.forEach((r) => newDates.push(chartOptions.value.xAxis.transform(r, option.id)));
    formatTimeSeriesChart({
      ...props.echartsOptions,
      xAxis: { ...props.echartsOptions.xAxis, data: newDates },
      series: props.echartsOptions.series.map((serie, i) => ({
        ...serie,
        data: [...props.echartsOptions.series[i].data],
      })),
    });
  }

  function downloadAs(action, option) {
    switch (option.id) {
      case DownloadActionType.IMAGE:
        downloadAsImg();
        break;
      case DownloadActionType.CSV:
        DownloadUtils.downloadAsCsv(props.name, rows.value.slice(1), colTypes.value, rows.value[0]);
        break;
      case DownloadActionType.EXCEL:
        DownloadUtils.downloadAsXlsx(props.name, rows.value.slice(1), colTypes.value, rows.value[0]);
        break;
      default:
        throw new Exception('Invalid download type');
    }
  }

  function downloadAsImg() {
    const echartsInstanceDOM = chartContent.value.$el.querySelector('.echarts') as HTMLElement;

    if (!echartsInstanceDOM) throw new Exception('Cannot reach DOM instance to download image');

    const chartInstance = getInstanceByDom(echartsInstanceDOM);
    const chartWidth = chartInstance.getWidth();
    const dashboardWidth = document.getElementById('main-content').offsetWidth;
    const chartPercentageRatio = (chartWidth * 100) / dashboardWidth;
    const chartRatio = chartPercentageRatio / 90; // 90 represents 90%, which is the approximate size a chart that takes all the space in a card
    const pixelRatio = 1 - chartRatio <= 0 ? 1 : 1 + chartRatio;
    const base64Url = chartInstance.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' });
    DownloadUtils.downloadAsImg(props.name, base64Url);
  }

  function formatTimeSeriesChart(options) {
    debounce(
      () => {
        if (props.highlightCurrentPeriod === false) {
          chartOptions.value = { ...options, yAxis: formatYAxis(options.xAxis.granularity) };
          return;
        }
        // we want to check if the data is within our current period (defined by granularity) so we can highlight it
        // this will help the user understand the period is not done and the lower numbers may be normal
        const currentPeriodIdx = options.xAxis.raw.findIndex((d) => isCurrentPeriod(d, options.xAxis.granularity));
        // due to some timezone magic, the backend is sometimes returning up to 2 days in the future, we don't want to show this data to the user
        const futurePeriodIdx = options.xAxis.raw.findIndex((d) => isFromTheFuture(d));
        const times = futurePeriodIdx > -1 ? options.xAxis.data.slice(0, futurePeriodIdx) : options.xAxis.data;
        // we are not in the current period, no treatment to do, return the options as is
        if (currentPeriodIdx === -1 || props.dontTrimDataFromChart) {
          chartOptions.value = { ...options, yAxis: formatYAxis(options.xAxis.granularity) };
          return;
        }
        const newSeries = [];
        options.series.forEach((series) => {
          const continuousChartTypes = [ChartType.LINE]; // we need to link previous and current period to draw a line, but not for other types of chart
          const separatorSlice =
            series.type.indexOf(continuousChartTypes) > -1 ? currentPeriodIdx - 1 : currentPeriodIdx;
          const previousPeriod = series.data.slice(0, currentPeriodIdx);
          const currentPeriod = series.data
            .map((d, i) => (i >= separatorSlice ? d : null))
            .slice(0, futurePeriodIdx > -1 ? futurePeriodIdx : series.data.length);
          const inProgressName = `${series.name} (${i18n.global.t('messages.inProgress')})`;
          switch (series.type) {
            case ChartType.LINE:
              // Push data from previous period with no changes
              newSeries.push({ ...series, data: previousPeriod });
              // Push data for current period with dotted line and adding (in progress) in the name.
              newSeries.push({
                ...series,
                data: currentPeriod,
                lineStyle: { type: 'dotted' },
                name: inProgressName,
                stack: inProgressName,
              });
              break;
            case ChartType.BAR:
              if (series.name !== capitalize(i18n.global.t('messages.currentDailyCap'))) {
                // Push data from previous period with no changes
                newSeries.push({ ...series, data: previousPeriod, stack: series.name });
                // Push data for current period with dotted line and adding (in progress) in the name.
                newSeries.push({
                  ...series,
                  data: currentPeriod,
                  itemStyle: { opacity: 0.5 },
                  name: inProgressName,
                  stack: series.name,
                });
              } else {
                newSeries.push(series);
              }
              break;
            default:
              newSeries.push(...options.series);
              break;
          }
        });
        chartOptions.value = {
          ...options,
          series: newSeries,
          xAxis: { ...options.xAxis, data: times },
          yAxis: formatYAxis(options.xAxis.granularity),
        };
      },
      100,
      0,
    )();
  }

  function formatYAxis(granularity) {
    if (!props.echartsOptions?.yAxis) return props.echartsOptions.yAxis;

    const formatName = (yAxis) => ({
      ...yAxis,
      name:
        yAxis?.name && granularity
          ? `${yAxis.name.toUpperCase()}/${i18n.global.t(`names.${granularity}`, 1).toUpperCase()}`
          : yAxis?.name,
    });

    if (props.echartsOptions.yAxis instanceof Array) {
      return props.echartsOptions.yAxis.map((yAxis) => formatName(yAxis));
    }

    return formatName(props.echartsOptions.yAxis);
  }

  function goFullscreen() {
    emit(ChartEvents.GO_FULLSCREEN);
  }

  function init() {
    setupActions();
    if (props.echartsOptions != null) {
      applyOptions();
      triggerCallbacks();
    }
  }

  function intervalSelection(action, interval) {
    if (!action?.transform) throw new Exception('transform method is missing');

    const format = DateFormatUtils.getDateFormatByTimeGranularity(interval.id);
    const groupByGranularity = {};
    const raw = [];
    props.echartsOptions.xAxis.raw.forEach((d, i) => {
      const millis = DateTime.fromMillis(d);
      const formatted = typeof format === 'string' ? millis.toFormat(format) : millis.toLocaleString(format);
      if (!groupByGranularity[formatted]) {
        groupByGranularity[formatted] = [];
        raw.push(d);
      }
      groupByGranularity[formatted].push(i);
    });

    const trendLine = chartOptions.value.series.find((serie) => serie.isTrend);
    let trendLineSerie = null;

    let dates = [];
    const newSeries = [];
    props.echartsOptions.series.forEach((serie, j) => {
      const chartOptionsSerie = chartOptions.value.series[j];
      const newSerie = [];
      Object.keys(groupByGranularity).forEach((key, index) => {
        let value;
        if (serie.name?.toLowerCase() === i18n.global.t('messages.currentDailyCap').toLowerCase()) {
          const cellData = serie.data[index];
          value = getChartCellValue(cellData);
        } else {
          value = action.transform(
            groupByGranularity[key].map((k) => {
              const cellData = serie.data[k];
              return getChartCellValue(cellData);
            }),
          );
        }
        dates.push(key);
        newSerie.push({
          value,
          name:
            serie.data[index] != null &&
            chartOptionsSerie.data[index] != null &&
            typeof chartOptionsSerie.data[index] === 'object'
              ? chartOptionsSerie.data[index].name
              : chartOptionsSerie.name,
          itemStyle:
            serie.data[index] != null &&
            chartOptionsSerie.data[index] != null &&
            typeof chartOptionsSerie.data[index] === 'object'
              ? chartOptionsSerie.data[index].itemStyle
              : chartOptionsSerie.type,
        });
      });
      newSeries.push(newSerie);
    });

    dates = dates.filter((v, i, a) => a.indexOf(v) === i);

    if (trendLine) {
      trendLineSerie = { ...trendLine, data: [] };
      Object.keys(groupByGranularity).forEach((key) => {
        const value = action.transform(
          groupByGranularity[key].map((k) => {
            const cellData = trendLine.data[k];
            return getChartCellValue(cellData);
          }),
        );
        trendLineSerie.data.push(value);
      });
    }

    const series = props.echartsOptions.series.map((serie, i) => ({
      ...serie,
      data: newSeries[i],
      type: chartOptions.value.series[i].type,
    }));
    if (trendLineSerie) {
      series.push(trendLineSerie);
    }

    formatTimeSeriesChart({
      ...props.echartsOptions,
      xAxis: TimeseriesAxisX.fromObject({
        ...props.echartsOptions.xAxis,
        data: dates,
        raw,
        granularity: interval.id,
      }),
      yAxis: formatYAxis(interval.id),
      series,
    });
    emit(ChartEvents.CHART_GRANULARITY_UPDATED, interval.id);
  }

  function isCurrentPeriod(date, granularity) {
    const zone = selectedTimezone.value === ChartTimezoneType.UNIT_LOCAL ? 'local' : 'UTC';
    const today = DateTime.local({ zone }).endOf('day');
    const list = [TimeGranularity.HOURLY, TimeGranularity.DAILY, TimeGranularity.MONTHLY, TimeGranularity.YEARLY];
    const checkAgainst = list.splice(list.findIndex((g) => g === granularity));
    const res = checkAgainst.map((c) => DateTime.fromMillis(date, { zone }).hasSame(today, c));
    return res.filter((r) => r).length === checkAgainst.length;
  }

  function isFromTheFuture(dateMs) {
    if (props.showFuture) return false; // if we explicitly want to show the future, return false here
    const zone = selectedTimezone.value === ChartTimezoneType.UNIT_LOCAL ? 'local' : 'UTC';
    const date = DateTime.fromMillis(dateMs, { zone });
    const todayEndOfDay = DateTime.local({ zone }).endOf('day');
    return date >= todayEndOfDay;
  }

  function optionSelected({ action, option }) {
    groupActions.value = groupActions.value.map((groupAction) => ({
      ...groupAction,
      options: groupAction.options.map((o) => ({
        ...o,
        isSelected: groupAction.id === action.id ? o.id === option.id && action.isSelectable : o.isSelected,
      })),
    }));
    availableActions[action.id](action, option);
    saveChartOptionInLocalStorage();
    if (action.callback === 'changeTimezone') {
      emit(ChartEvents.TIMEZONE_SELECTED, option);
    }
  }

  function reset() {
    LocalCache.remove(localCacheKey.value);
    setChartOptions();
  }

  function setChartOptions() {
    setupActions();
    applyOptions();
  }

  function saveChartOptionInLocalStorage() {
    if (JSON.stringify(groupActions.value) === JSON.stringify(props.actions)) {
      LocalCache.remove(localCacheKey.value);
    } else {
      const settings = [];
      groupActions.value.forEach((action) => {
        const optionSelected = action.options.find((option) => option.isSelected);
        if (optionSelected !== undefined && optionSelected !== null) {
          settings.push({
            id: action.id,
            optionSelected: optionSelected.id,
          });
        }
      });

      if (settings?.length) {
        LocalCache.set(localCacheKey.value, settings);
      }
    }
  }

  function getLocalCacheForCharts() {
    return LocalCache.get(localCacheKey.value);
  }

  function setupActions() {
    const savedSettings = getLocalCacheForCharts();

    if (props.actions) {
      groupActions.value = [
        ...props.actions.map((action) => {
          const savedSetting = savedSettings?.find((stg) => stg.id === action.id);
          // in some case, user may have an old LS with selected option that does not exist anymore. If this is the case, use the default selection
          const doesOptionSelectedExists = !!action?.options?.find((o) => savedSetting?.optionSelected === o.id);
          return {
            ...action,
            options: [
              ...action.options.map((o) => ({
                ...o,
                isSelected: doesOptionSelectedExists ? savedSetting.optionSelected === o.id : o.isSelected,
              })),
            ].sort((a, b) => a.rank - b.rank),
          };
        }),
      ]
        .filter((a) => (props.isFullscreen ? a.id !== ChartActionId.FULLSCREEN : true))
        .sort((a, b) => a.rank - b.rank);
    }
    saveChartOptionInLocalStorage();
  }

  function showTrend(action, option) {
    const trendSerie = {
      name: null,
      data: null,
      cellType: CellType.INTEGER,
      type: ChartType.LINE,
      color: '#ff0083',
      lineStyle: { type: 'dashed', opacity: 0.5 },
      areaStyle: null,
      symbol: 'none',
      emphasis: null,
      isTrend: true,
    };
    if (chartOptions.value == null) return;
    const { granularity } = chartOptions.value.xAxis;

    switch (option.id) {
      case ChartTrendType.NONE:
        chartOptions.value.series = [...chartOptions.value.series.filter((serie) => !serie?.isTrend)];
        break;
      case ChartTrendType.TREND:
        chartOptions.value.series = [
          ...chartOptions.value.series.filter((serie) => !serie?.isTrend),
          ...props.echartsOptions.series.map((serie) => ({
            ...trendSerie,
            name: capitalize(i18n.global.t('messages.showTrendTrue')),
            data: ChartTrendActionsUtils.computeTrend(
              chartOptions.value.xAxis.raw,
              serie.data,
              ChartTrendActionsUtils.getScaleByGranularity(granularity),
              granularity,
            ),
            emphasis: null,
          })),
        ];
        break;
      case ChartTrendType.LINEAR:
      case ChartTrendType.POLYNOMIAL:
      case ChartTrendType.EXPONENTIAL:
      case ChartTrendType.LOGARITHMIC:
        chartOptions.value.series = [
          ...chartOptions.value.series.filter((serie) => !serie?.isTrend),
          ...props.echartsOptions.series.map((serie) => ({
            ...trendSerie,
            name: capitalize(i18n.global.t('messages.showTrendLinear')),
            data: ChartTrendActionsUtils.regressionAnalysis(option.id, props.echartsOptions.xAxis.raw, serie),
            isTrend: true,
            emphasis: null,
          })),
        ];
        break;
      default:
        break;
    }
  }

  function triggerCallbacks() {
    if (groupActions.value && chartOptions.value) {
      groupActions.value.forEach((action) => {
        action.options
          .filter((o) => o.isSelected)
          .forEach((o) => {
            availableActions[action.id](action, o);
          });
      });
    }
  }

  function updatePageIndex(idx) {
    pageIndex.value = idx;
  }

  function updatePageSize(val) {
    pageSize.value = val;
  }

  function showDailyCap() {
    emit(ChartEvents.SHOW_DAILY_CAP);
  }

  defineExpose({ intervalSelection });
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .chart-wrapper {
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: column;
    position: relative;
    align-items: center;
    justify-content: center;

    .content {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;

      &.has-sidebar {
        display: grid;
        grid-template-columns: 80% 20%;

        .body {
          flex-grow: 1;
          padding: 1rem;
          grid-row: 1;
        }
      }

      .body {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
      }
    }

    .chart-content {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      position: relative;
      margin-top: 1rem;
      overflow: hidden;
    }

    &.none {
      .echarts {
        margin-top: -1rem;
      }
    }

    .echarts {
      min-height: 27.2rem;
      height: 100%;
      width: 100%;
    }

    .chart-loader {
      position: absolute;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9;
    }
  }
</style>
